// utils/messenger.controller.js

import supabase from '../config/supabaseClient';

// Create a new conversation
export const createConversation = async (data) => {
  const { data: conversation, error } = await supabase
    .from('conversations')
    .insert([{
      user_id: data.user_id,
      subject: data.subject || 'Support Conversation',
      status: 'open',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }])
    .select();

  if (error) {
    throw new Error(`Error creating conversation: ${error.message}`);
  }

  return conversation;
};

// Get all conversations for a user
export const getConversations = async (userId) => {
  const { data, error } = await supabase
    .from('conversations')
    .select('*')
    .eq('user_id', userId)
    .order('updated_at', { ascending: false });

  if (error) {
    throw new Error(`Error fetching conversations: ${error.message}`);
  }

  return data;
};

// Get a specific conversation by ID
export const getConversationById = async (conversationId) => {
  const { data, error } = await supabase
    .from('conversations')
    .select('*')
    .eq('id', conversationId)
    .single();

  if (error) {
    throw new Error(`Error fetching conversation: ${error.message}`);
  }

  return data;
};

// Update a conversation
export const updateConversation = async (conversationId, data) => {
  const { data: updatedConversation, error } = await supabase
    .from('conversations')
    .update({
      ...data,
      updated_at: new Date().toISOString()
    })
    .eq('id', conversationId)
    .select();

  if (error) {
    throw new Error(`Error updating conversation: ${error.message}`);
  }

  return updatedConversation;
};

// Close a conversation
export const closeConversation = async (conversationId) => {
  return updateConversation(conversationId, { status: 'closed' });
};

// Save a message
export const saveMessage = async (data) => {
  // Default admin user ID - in a real app you might want to fetch this from a settings table
  const ADMIN_USER_ID = 47; 

  const { data: message, error } = await supabase
    .from('messages')
    .insert([{
      conversation_id: data.conversation_id,
      sender_user_id: data.user_id,
      receiver_user_id: ADMIN_USER_ID, 
      message_body: data.message,
      timestamp: new Date().toISOString()
    }])
    .select();

  if (error) {
    throw new Error(`Error saving message: ${error.message}`);
  }

  // Update conversation timestamp
  await updateConversation(data.conversation_id, {});

  return message;
};

// Save admin reply
export const saveAdminReply = async (data) => {
  const { data: message, error } = await supabase
    .from('messages')
    .insert([{
      conversation_id: data.conversation_id,
      sender_user_id: data.admin_id,
      receiver_user_id: data.user_id,
      message_body: data.message,
      timestamp: new Date().toISOString()
    }])
    .select();

  if (error) {
    throw new Error(`Error saving admin reply: ${error.message}`);
  }

  // Update conversation timestamp
  await updateConversation(data.conversation_id, {});

  return message;
};

// Get all messages for a conversation
export const getMessagesByConversationId = async (conversationId) => {
  const { data, error } = await supabase
    .from('messages')
    .select('*')
    .eq('conversation_id', conversationId)
    .order('timestamp', { ascending: true });

  if (error) {
    throw new Error(`Error fetching messages: ${error.message}`);
  }

  return data;
};